import { useCallback, useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { MessageCircleQuestion, Loader2, Check, X, Trash2, Pencil, ShieldCheck, RefreshCw } from "lucide-react";
import { toast } from "sonner";

function UnansweredCard({ q, onDone }) {
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (answer.trim().length < 2) return toast.error("Please write an answer");
    setBusy(true);
    try {
      await api.post(`/ai-tutor/unanswered/${q.id}/answer`, { answer: answer.trim() });
      toast.success("Answer saved to the tutor cache");
      onDone();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Failed"); } finally { setBusy(false); }
  };
  const dismiss = async () => {
    setBusy(true);
    try { await api.post(`/ai-tutor/unanswered/${q.id}/dismiss`); toast.success("Dismissed"); onDone(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Failed"); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 space-y-3" data-testid={`unanswered-${q.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[11px] text-slate-400">{q.subject_name} · {q.lesson_title} · Class {q.class_name}{q.section ? `-${q.section}` : ""}</div>
          <div className="font-medium text-slate-900 dark:text-slate-100 mt-0.5">{q.question}</div>
          <div className="text-[11px] text-slate-400 mt-1">Asked {q.ask_count}× · by {q.asked_by_name || "student"}</div>
        </div>
      </div>
      <Textarea value={answer} onChange={(e) => setAnswer(e.target.value)} rows={3}
        placeholder="Write the answer students will see (and hear)…" className="rounded-xl" data-testid={`answer-input-${q.id}`} />
      <div className="flex gap-2">
        <Button onClick={submit} disabled={busy} className="bg-emerald-500 hover:bg-emerald-600 rounded-full" data-testid={`answer-save-${q.id}`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4 mr-1" />} Save answer
        </Button>
        <Button onClick={dismiss} disabled={busy} variant="outline" className="rounded-full text-slate-500" data-testid={`answer-dismiss-${q.id}`}>
          <X className="h-4 w-4 mr-1" /> Dismiss
        </Button>
      </div>
    </div>
  );
}

function CacheCard({ c, onDone }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(c.answer);
  const [busy, setBusy] = useState(false);
  const act = async (fn, msg) => { setBusy(true); try { await fn(); toast.success(msg); onDone(); } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Failed"); } finally { setBusy(false); } };
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 space-y-2" data-testid={`cache-${c.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] text-slate-400">{c.subject_name} · {c.lesson_title} · Class {c.class_name}</div>
        <div className="flex items-center gap-1.5">
          <span className={`text-[10px] px-2 py-0.5 rounded-full ${c.approved ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"}`}>{c.approved ? "Approved" : "Pending"}</span>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500">{c.source}</span>
        </div>
      </div>
      <div className="font-medium text-slate-900 dark:text-slate-100 text-sm">{c.question === "[LESSON_INTRO]" ? "Lesson introduction" : c.question}</div>
      {editing ? (
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} className="rounded-xl" data-testid={`cache-edit-${c.id}`} />
      ) : (
        <div className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap line-clamp-4">{c.answer}</div>
      )}
      <div className="flex flex-wrap gap-2 pt-1">
        {editing ? (
          <>
            <Button size="sm" onClick={() => act(() => api.put(`/ai-tutor/cache/${c.id}`, { answer: text }), "Answer updated")} disabled={busy} className="rounded-full bg-sky-500 hover:bg-sky-600" data-testid={`cache-save-${c.id}`}><Check className="h-4 w-4 mr-1" /> Save</Button>
            <Button size="sm" variant="outline" onClick={() => { setEditing(false); setText(c.answer); }} className="rounded-full">Cancel</Button>
          </>
        ) : (
          <>
            {c.approved
              ? <Button size="sm" variant="outline" onClick={() => act(() => api.post(`/ai-tutor/cache/${c.id}/unapprove`), "Unapproved")} disabled={busy} className="rounded-full" data-testid={`cache-unapprove-${c.id}`}>Unapprove</Button>
              : <Button size="sm" onClick={() => act(() => api.post(`/ai-tutor/cache/${c.id}/approve`), "Approved")} disabled={busy} className="rounded-full bg-emerald-500 hover:bg-emerald-600" data-testid={`cache-approve-${c.id}`}><ShieldCheck className="h-4 w-4 mr-1" /> Approve</Button>}
            <Button size="sm" variant="outline" onClick={() => setEditing(true)} className="rounded-full" data-testid={`cache-edit-btn-${c.id}`}><Pencil className="h-4 w-4 mr-1" /> Edit</Button>
            <Button size="sm" variant="ghost" onClick={() => act(() => api.delete(`/ai-tutor/cache/${c.id}`), "Deleted")} disabled={busy} className="rounded-full text-rose-600 hover:bg-rose-50" data-testid={`cache-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
          </>
        )}
      </div>
    </div>
  );
}

export default function TutorReview({ embedded = false }) {
  const [tab, setTab] = useState("unanswered");
  const [unanswered, setUnanswered] = useState([]);
  const [cache, setCache] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, c] = await Promise.all([
        api.get("/ai-tutor/unanswered", { params: { status: "pending" } }),
        api.get("/ai-tutor/cache", { params: { approved: "all" } }),
      ]);
      setUnanswered(u.data.items || []);
      setCache(c.data.items || []);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Failed to load"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const content = (
      <div className="space-y-6 max-w-4xl" data-testid="tutor-review-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            {!embedded && (
              <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
                <MessageCircleQuestion className="h-7 w-7 text-violet-500" /> AI Tutor Review
              </h1>
            )}
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Answer questions the tutor couldn't, and manage the answer cache.</p>
          </div>
          <Button variant="outline" onClick={load} className="rounded-full" data-testid="tutor-review-refresh"><RefreshCw className="h-4 w-4 mr-1.5" /> Refresh</Button>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="tutor-review-tabs">
            <TabsTrigger value="unanswered" data-testid="tab-unanswered">Unanswered ({unanswered.length})</TabsTrigger>
            <TabsTrigger value="cache" data-testid="tab-cache">Answer Cache ({cache.length})</TabsTrigger>
          </TabsList>

          {loading ? (
            <div className="py-16 text-center text-slate-400"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div>
          ) : (
            <>
              <TabsContent value="unanswered" className="space-y-3 mt-4">
                {unanswered.length === 0 && <p className="text-sm text-slate-400 py-8 text-center">No pending questions. 🎉</p>}
                {unanswered.map((q) => <UnansweredCard key={q.id} q={q} onDone={load} />)}
              </TabsContent>
              <TabsContent value="cache" className="space-y-3 mt-4">
                {cache.length === 0 && <p className="text-sm text-slate-400 py-8 text-center">The answer cache is empty.</p>}
                {cache.map((c) => <CacheCard key={c.id} c={c} onDone={load} />)}
              </TabsContent>
            </>
          )}
        </Tabs>
      </div>
  );

  if (embedded) return content;
  return (
    <Layout>
      {content}
    </Layout>
  );
}
